import { animate, style, transition, trigger } from '@angular/animations';
import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ProgressSpinner } from 'primeng/progressspinner';
import PlayerSampleView from '../../../../types/player/PlayerSampleView';
import { PlayerService } from '../../services/player.service';

@Component({
    selector: 'app-player',
    imports: [
        NgClass,
        ProgressSpinner
    ],
    templateUrl: './player.component.html',
    styleUrl: './player.component.scss',
    animations: [
        trigger('pageChangeAnimation', [
            transition(':increment', [
                style({ opacity: 0, transform: 'translateX(50px)' }), // Start off-screen right
                animate('0.5s ease-in-out', style({ opacity: 1, transform: 'translateX(0)' })) // Fade-in from right
            ]),
            transition(':decrement', [
                style({ opacity: 0, transform: 'translateX(-50px)' }), // Start off-screen left
                animate('0.5s ease-in-out', style({ opacity: 1, transform: 'translateX(0)' })) // Fade-in from left
            ])
        ])
    ]
})
export class PlayerComponent implements OnInit, OnDestroy {

    public sampleView: PlayerSampleView | undefined = undefined;
    public token: string | undefined = undefined;
    public loading: boolean = true;
    public error: boolean = false;

    public paginatedVehicles: any[] = [];
    public currentPage: number = 0;
    private pageSize: number = 10;
    private intervalId: any;

    constructor(
        private playerService: PlayerService,
        private route: ActivatedRoute
    ) {
    }

    /**
     * Initializes the component.
     */
    public ngOnInit(): void {
        this.route.queryParams.subscribe(params => {
            const token = params['token'];
            if (token) {
                this.token = token;
                this.loadPlayerView(token);
            }
        });
    }

    /**
     * Cleans up the interval when the component is destroyed.
     */
    public ngOnDestroy(): void {
        clearInterval(this.intervalId);
    }

    /**
     * Load the player view.
     *
     * @param token The token to access the API.
     */
    private loadPlayerView(token: string): void {
        this.loading = true;
        this.playerService.getSampleView(token).then((view) => {
            this.sampleView = view;
            this.startAutoPagination();
        }).catch((error) => {
            console.error('Failed to load player view:', error);
            this.error = true;
        }).finally(() => {
            this.loading = false;
        });
    }

    /**
     * Start automatic pagination every 10 seconds.
     */
    private startAutoPagination(): void {
        this.updatePaginatedVehicles();
        this.intervalId = setInterval(() => {
            this.currentPage = ( this.currentPage + 1 ) % this.totalPages;
            this.updatePaginatedVehicles();
        }, 10000);
    }

    /**
     * Updates the displayed vehicles based on the current page.
     */
    private updatePaginatedVehicles(): void {
        if (!this.sampleView?.vehicles) {
            this.paginatedVehicles = [];
            return;
        }

        const start = this.currentPage * this.pageSize;
        this.paginatedVehicles = this.sampleView.vehicles.slice(start, start + this.pageSize);
    }

    /**
     * Returns an array representing the total number of pages for iteration.
     */
    get totalPagesArray(): number[] {
        return Array.from({ length: this.totalPages }, (_, i) => i);
    }

    /**
     * Get the total number of pages based on vehicles count.
     */
    get totalPages(): number {
        return this.sampleView?.vehicles ? Math.ceil(this.sampleView.vehicles.length / this.pageSize) : 1;
    }
}
